<template>
  <v-card id="summary-card" class="py-4 px-5 my-0 elevation-0 full-height">
    <div class="summary-card-header items-flex space-between">
      <div
        class="summary-card-header-title"
        :class="{
          'secondary--text': !$vuetify.theme.dark,
          'white--text': $vuetify.theme.dark,
        }"
      >
        {{ title }}
        <InfoIcon v-if="infoText" :text="infoText" :size="14" />
      </div>
      <div v-if="headerLink" class="summary-card-header-link">
        <a @click="$emit('headerLinkClicked')">{{ headerLink }}</a>
      </div>
    </div>

    <div v-if="loading" class="bar-loading">
      <BarLoading hide-details />
    </div>
    <template v-else>
      <div class="summary-card-value items-flex mt-2">
        <div
          class="summary-card-value-number"
          :class="{
            'softGreen--text': value > 0 && valuePositiveGreen,
            'secondary--text': value > 0 && !valuePositiveGreen,
            'secondary--text': value === 0,
            'softRed--text': value < 0,
          }"
        >
          <currency
            v-if="(value || value === 0) && valueCurrency"
            :value="value"
            :currency="valueCurrency"
            :showPositive="valueShowPositive"
          />
          <span v-else-if="value || value === 0">{{ value }}</span>
          <span v-else>-</span>
        </div>
        <div class="ml-2">
          <div
            class="summary-card-value-comparison items-flex"
            :class="{
              'positive-percent': comparisonPercent > 0,
              'negative-percent': comparisonPercent < 0,
              'zero-percent': comparisonPercent === 0,
            }"
          >
            <v-icon
              v-if="comparisonPercent > 0"
              size="15"
              :color="$vuetify.theme.dark ? 'white' : 'darkGreen'"
            >
              mdi-arrow-up
            </v-icon>
            <v-icon
              v-else-if="comparisonPercent < 0"
              size="15"
              :color="$vuetify.theme.dark ? 'white' : 'darkRed'"
            >
              mdi-arrow-down
            </v-icon>
            <template v-if="comparisonPercent || comparisonPercent === 0">
              <currency neverHide :value="comparisonPercent" />
              %
            </template>
          </div>

          <div
            class="summary-card-value-comparison comparison-net"
            :class="{
              'darkGreen--text': comparisonNet > 0,
              'darkRed--text': comparisonNet < 0,
            }"
          >
            <currency
              v-if="comparisonNet || comparisonNet === 0"
              :value="comparisonNet"
              :currency="user.baseCurrency"
              showPositive
            />
          </div>
        </div>
      </div>

      <div v-if="subtitle" class="summary-card-subtitle darkTextGrey--text">
        {{ subtitle }}
      </div>
    </template>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import InfoIcon from '@/views/components/common/InfoIcon'
import BarLoading from '@/views/components/core/BarLoading.vue'
import Currency from '@/views/components/common/Currency'

export default {
  components: { InfoIcon, BarLoading, Currency },
  props: {
    title: {
      type: String,
      default: undefined,
    },
    infoText: {
      type: String,
      default: undefined,
    },
    loading: {
      type: Boolean,
      required: false,
    },
    value: {
      type: null,
      default: null,
    },
    valueCurrency: {
      type: String,
      default: null,
    },
    valueShowPositive: {
      type: Boolean,
      default: false,
    },
    valuePositiveGreen: {
      type: Boolean,
      default: false,
    },
    comparisonPercent: {
      type: null,
      default: null,
    },
    comparisonNet: {
      type: null,
      default: null,
    },
    subtitle: {
      type: String,
      default: undefined,
    },
    headerLink: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapState(['user']),
  },
}
</script>

<style lang="scss">
#summary-card {
  .items-flex {
    display: flex;
    align-items: center;
    &.space-between {
      justify-content: space-between;
    }
  }
  &.full-height {
    height: 100%;
  }
  .summary-card-header {
    .summary-card-header-title {
      font-size: 14px;
      font-weight: 500;
      i {
        margin-bottom: 1px;
      }
    }
    .summary-card-header-link {
      font-size: 13px;
    }
  }
  .summary-card-value-number {
    font-size: 26px;
    font-weight: 600;
    font-family: Verdana, Geneva, Tahoma, sans-serif;
  }
  .summary-card-value-comparison {
    border-radius: 5px;
    padding: 1px 6px 1px 5px;
    &.positive-percent {
      background: #e6f4ea;
      color: #137333;
      font-size: 12px;
      font-weight: 500;
    }
    &.negative-percent {
      background: #fce8e6;
      color: #a50e0e;
      font-size: 12px;
      font-weight: 500;
    }
    &.zero-percent {
      background: #f7f8fb;
      color: #000;
      font-size: 12px;
      font-weight: 500;
    }
    &.comparison-net {
      margin-top: 2px;
      font-size: 12px;
    }
  }
  .summary-card-subtitle {
    font-size: 14px;
    font-weight: 500;
  }
}

.theme--dark {
  #summary-card {
    .summary-card-value-comparison {
      &.positive-percent {
        background: #174b35;
        color: white;
      }
      &.negative-percent {
        background: #5d1f1a;
        color: white;
      }
      &.zero-percent {
        background: #5b5b5b;
        color: white;
      }
    }
  }
}
</style>
